/**
 * Downloading a workspace artifact. Shared by the Artifacts section and the chat's artifact side
 * panel: the content route is bearer-authed, so a plain link would 401 — the bytes are fetched here and
 * handed to the browser as an object URL.
 */
import { notify } from '@/lib/notify.js';
import {
  fetchWorkspaceArtifactBlob,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';

/**
 * Download an artifact via the bearer-authed content route → object URL → synthetic anchor click.
 *
 * `revision` names a stored version to download instead of the newest one. The viewer passes whatever it
 * is showing: while an older version is on screen, downloading the head would hand the user bytes they
 * are not looking at.
 */
export async function triggerArtifactDownload(
  artifact: WorkspaceArtifact,
  revision?: number,
): Promise<void> {
  const blob = await fetchWorkspaceArtifactBlob(
    artifact.workspaceId,
    artifact.id,
    revision,
  );
  saveBlobAs(blob, artifact.name);
}

/** Save bytes already in hand as a file of this name: object URL → synthetic anchor click. */
export function saveBlobAs(blob: Blob, name: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  /** Deferred so the browser can start the download first — same idiom as SkillDetail/runReportPdf. */
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/** Fire a single download, surfacing any failure as a toast (fire-and-forget from a menu/button). */
export function safeDownload(
  artifact: WorkspaceArtifact,
  revision?: number,
): void {
  void triggerArtifactDownload(artifact, revision).catch((error: unknown) =>
    notify.error(
      error instanceof Error ? error.message : 'Failed to download file',
    ),
  );
}
